'use client'

import { useReferencesProduct } from '@/feature/product/hooks/useReferencesProduct'
import { productArticlesAtom } from '@/feature/product/services/product.store'
import {
  referencesProductArticlesAtom,
  referencesProductArticlesFilteredAtom,
  referencesProductArticlesFilteredReferencesAtom,
} from '@/feature/product/services/referencesProduct.store'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Section } from '@/ui/layouts/section/Section'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  type ProductArticleDetails_ArticleFragment,
  type ProductAvailabilities_PickupPointOfSaleFragment,
  type ReferencesProduct_CurrentUserQuery,
  type ReferencesProductTable_ArticleFragment,
  ReferencesProductTableRows_PriceAndStockDocument,
  type ReferencesProductTableRows_PriceAndStockFragment,
} from '@gql-generated/graphql'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect } from 'react'
import { ReferencesProductColumnsMenu } from './ReferencesProductColumnsMenu.client'
import { ReferencesProductRows } from './ReferencesProductRows.client'

gql(/* GraphQL */ `
  fragment ReferencesProductTable_Article on Product {
    urlKey
    reference
    name
    type
    supplierReference
    brand {
      name
      urlKey
    }
    mainImage {
      ...FullPicture
    }
    additionalTechnicalSpecs {
      ...ProductTechnicalSpecs_TechnicalSpecs
    }
  }
`)

gql(/* GraphQL */ `
  fragment ReferencesProductTableRows_PriceAndStock on Product {
    ...ProductCalculatedPrice
    reference
    parentReference
    price {
      ...FullPrice
      ...TiersPrice
    }
    stock {
      isPriceAvailable
      isSellable
      packaging {
        ...FullPackaging
      }
    }
    isEquivalentProductsLinkVisible
    canAddToCart
    clickAndCollectStatus
    ...ProductAvailabilities_Product
  }
`)

gql(/* GraphQL */ `
  query ReferencesProductTableRows_PriceAndStock($references: [String!]!) {
    productsByReferences(references: $references) {
      ...ReferencesProductTableRows_PriceAndStock
    }
  }
`)

export type ReferencesProductTableProps = {
  articles: (ReferencesProductTable_ArticleFragment & ProductArticleDetails_ArticleFragment)[]
  currentUser?: ReferencesProduct_CurrentUserQuery['currentUser'] | null
  pickupPointOfSale?: ProductAvailabilities_PickupPointOfSaleFragment | null
}

export function ReferencesProductTable({
  articles,
  currentUser,
  pickupPointOfSale,
}: ReferencesProductTableProps) {
  const { t } = useTranslation(['productPage', 'referencesProductTable'])

  const setProductArticles = useSetAtom(productArticlesAtom)
  const { data: articlesPriceAndStock, loading: articlesPriceAndStockLoading } = useQuery(
    ReferencesProductTableRows_PriceAndStockDocument,
    {
      ...APOLLO_CLIENT_SIDE_OPTIONS,
      variables: {
        references: articles.map((article) => {
          if (!article.reference) {
            console.error(
              `Article with name "${article.name}" is missing a reference. Please check the data source.`,
            )
          }

          return article.reference
        }),
      },
    },
  )

  const setArticlesFiltered = useSetAtom(referencesProductArticlesFilteredAtom)
  const [referencesProductArticles, setReferencesProductArticles] = useAtom(
    referencesProductArticlesAtom,
  )
  const referencesProductArticlesFiltered = useAtomValue(referencesProductArticlesFilteredAtom)

  const referencesProductArticlesFilteredReferences = useAtomValue(
    referencesProductArticlesFilteredReferencesAtom,
  )

  const { selectedColumns } = useReferencesProduct()

  const titleLabel =
    referencesProductArticlesFiltered.length === referencesProductArticles.length
      ? t('productPage:referencesProductSection.title', {
          count: articles.length,
        })
      : t('productPage:referencesProductSection.titleFiltered', {
          count: referencesProductArticlesFiltered.length,
          total: referencesProductArticles.length,
        })

  useEffect(() => {
    setArticlesFiltered(
      referencesProductArticlesFilteredReferences.length > 0
        ? referencesProductArticlesFilteredReferences
            .map((reference) => articles.find((article) => article.reference === reference))
            .filter(
              (
                article,
              ): article is ReferencesProductTable_ArticleFragment &
                ProductArticleDetails_ArticleFragment &
                ReferencesProductTableRows_PriceAndStockFragment => article !== undefined,
            )
        : articles,
    )
    setProductArticles(articles)
    setReferencesProductArticles(articles)
  }, [
    articles,
    referencesProductArticlesFilteredReferences,
    setArticlesFiltered,
    setProductArticles,
    setReferencesProductArticles,
    articlesPriceAndStock,
  ])

  if (!referencesProductArticlesFiltered || referencesProductArticlesFiltered.length === 0) {
    return null
  }

  return (
    <Section
      title={titleLabel}
      topRightOutlet={<ReferencesProductColumnsMenu />}
      topRightOutletClassName="hidden desktop:block"
      className="pt-2 desktop:pt-4"
    >
      <table className="-mt-2 w-full table-fixed border-separate border-spacing-y-2 border-brand-2-200 desktop:mt-0 desktop:table-auto desktop:border-collapse desktop:border-spacing-0 desktop:border-px desktop:bg-white">
        <thead>
          <tr className="hidden border-b-px border-b-brand-2-200 bg-brand-2-100 text-left desktop:table-row">
            <th className="p-2 font-normal">
              {t('referencesProductTable:tableHeaderCells.references')}
            </th>
            {selectedColumns.map((column) => (
              <th key={column} className="p-2 font-normal">
                {column}
              </th>
            ))}
            <th className="p-2 font-normal">
              {t('referencesProductTable:tableHeaderCells.availability')}
            </th>
            <th className="p-2 font-normal desktop:text-right forbidden-desktop-ref-table:text-left">
              {t('referencesProductTable:tableHeaderCells.price')}
            </th>
            <th className="hidden p-2 font-normal desktop:text-right forbidden-desktop-ref-table:table-cell">
              {t('referencesProductTable:tableHeaderCells.addToBasket')}
            </th>
          </tr>
        </thead>
        <tbody>
          <ReferencesProductRows
            isArticlesPriceAndStockLoading={articlesPriceAndStockLoading}
            currentUser={currentUser}
            articlesPriceAndStock={
              Array.isArray(articlesPriceAndStock?.productsByReferences)
                ? articlesPriceAndStock.productsByReferences.map((product) => ({
                    ...product,
                    stock: {
                      ...product.stock,
                      availability: product.stock?.availability ?? [],
                    },
                  }))
                : undefined
            }
            pickupPointOfSale={pickupPointOfSale}
          />
        </tbody>
      </table>
    </Section>
  )
}
